import React from 'react';
import { useAppNavigation } from '../../hooks/useAppNavigation';
import SubPages from '../../pages/subpages/SubPagesEnum';
import { cn } from '../../lib/utils';
import { getCookie } from '../../services/Cookies';
import TutorialStep from '../Tutorial';

export interface FMESidebarProps {
  currentView?: string;
  model?: any;
  currentScenario?: any;
  currentSensitivity?: any;
}

export default function FMESidebar({
  currentView = 'architecture',
  model,
  currentScenario,
  currentSensitivity
}: FMESidebarProps) {
  const navigateToPage = useAppNavigation();
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  const isDataReady = !!(model && currentScenario);

  // Build dashboard/financial-statements entity similar to classic view
  const buildNavigationEntity = () => {
    // Fallback to cookie if props not available
    if (!currentScenario || !model) {
      const entityCookie = getCookie("entity");
      if (entityCookie && entityCookie !== "" && entityCookie !== 'undefined') {
        return JSON.parse(entityCookie);
      }
      return null;
    }

    // Build entity like in classic view
    if (currentSensitivity && currentSensitivity !== 'null') {
      // Clone sensitivity to avoid mutations
      const entity = JSON.parse(JSON.stringify(currentSensitivity));
      entity.modelName = model.name;
      entity.type = 'sensitivity';
      entity.previous_entity = JSON.parse(JSON.stringify(model));
      entity.previous_entity.currentScenario = JSON.parse(JSON.stringify(currentScenario));
      return entity;
    } else {
      // Clone scenario to avoid mutations
      const entity = JSON.parse(JSON.stringify(currentScenario));
      entity.modelName = model.name;
      entity.type = 'scenario';
      entity.previous_entity = JSON.parse(JSON.stringify(model));
      entity.previous_entity.currentScenario = JSON.parse(JSON.stringify(currentScenario));
      return entity;
    }
  };

  const navItems = [
    {
      id: 'architecture',
      label: 'Architecture',
      icon: 'account_tree',
      onClick: () => {
        const entity = buildNavigationEntity();
        const model = entity?.previous_entity || entity;
        navigateToPage(SubPages.FinancialModelEditor, model);
      },
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'dashboard',
      onClick: () => navigateToPage(SubPages.Dashboard, buildNavigationEntity()),
    },
    {
      id: 'financial-statements',
      label: 'Financial statements',
      icon: 'description',
      onClick: () => navigateToPage(SubPages.FinancialStatements, buildNavigationEntity()),
    },
    {
      id: 'calculations',
      label: 'Calculation results',
      icon: 'calculate',
      onClick: () => navigateToPage(SubPages.CalculationResults, buildNavigationEntity()),
    },
    {
      id: 'compare',
      label: 'Compare',
      icon: 'compare_arrows',
      onClick: () => navigateToPage(SubPages.Compare, buildNavigationEntity()),
    },
    // {
    //   id: 'advisory',
    //   label: 'Advisory',
    //   icon: 'monitoring',
    //   onClick: () => {}, // Placeholder
    //   disabled: true,
    // },
    {
      id: 'audit',
      label: 'Audit',
      icon: 'search_insights',
      onClick: () => navigateToPage(SubPages.Audit, buildNavigationEntity()),
    },
    {
      id: 'history',
      label: 'History',
      icon: 'update',
      onClick: () => navigateToPage(SubPages.History, buildNavigationEntity()),
    },
    {
      id: 'tools',
      label: 'Tools',
      icon: 'construction',
      onClick: () => navigateToPage(SubPages.Tools, buildNavigationEntity()),
    },
        {
      id: 'AI',
      label: 'AI',
      icon: 'memory',
      onClick: () => navigateToPage(SubPages.AI, buildNavigationEntity()),
    },
  ];

  return (
    <aside className={cn(
      "bg-white border-r border-slate-200 flex flex-col shrink-0 transition-all duration-300",
      isCollapsed ? "w-16" : "w-64"
    )}>
      {/* Header */}
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        {!isCollapsed && (
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            Model Explorer
          </span>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="text-slate-400 hover:text-primary transition-colors"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <span className="material-symbols-outlined text-sm">
            {isCollapsed ? 'chevron_right' : 'side_navigation'}
          </span>
        </button>
      </div>

      {/* Navigation items */}
      <TutorialStep tutorial='main' identifier='sidebar_navigation' fixedCenter marginLeft={isCollapsed ? 70 : 270}>
        <nav className="flex-1 overflow-y-auto py-2">
          {navItems.map((item) => {
            const tutorialIdentifiers: Record<string, string> = {
              'dashboard': 'sidebar_dashboard',
              'financial-statements': 'sidebar_financial_statements',
              'calculations': 'sidebar_calculation_results',
              'audit': 'sidebar_audit',
              'history': 'sidebar_history',
              'tools': 'sidebar_tools',
              'compare': 'sidebar_compare',
              'AI': 'sidebar_ai',
            };

            const tutorialId = tutorialIdentifiers[item.id];

            const button = (
              <button
                key={item.id}
                onClick={item.onClick}
                disabled={item.disabled || (!isDataReady && item.id !== 'architecture')}
                title={isCollapsed ? item.label : undefined}
                className={cn(
                  'sidebar-item',
                  currentView === item.id && 'active',
                  item.disabled && 'opacity-50 cursor-not-allowed',
                  isCollapsed && 'justify-center px-3'
                )}
              >
                <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
                {!isCollapsed && (
                  <span className="flex items-center gap-2 flex-1">
                    {item.label}
                  </span>
                )}
              </button>
            );

            if (tutorialId) {
              return (
                <TutorialStep key={item.id} tutorial='main' identifier={tutorialId} display='block' position="none" marginLeft={isCollapsed ? 70 : 270} marginTop={-40}>
                  {button}
                </TutorialStep>
              );
            }

            return <React.Fragment key={item.id}>{button}</React.Fragment>;
          })}
        </nav>
      </TutorialStep>
    </aside>
  );
}
